import type { ApiResponse, WorkspaceResearchContext } from '@bower/shared';
import type { HttpClient } from './http-client.types';
import type { WorkspaceLogoBackground } from '@bower/shared/utils/workspaceLogoTile';

export interface CreateWorkspaceParams {
  name: string;
  institution?: string;
  description?: string;
  /** Onboarding wizard v2 only — triggers workspace seeding. */
  seedOnboarding?: boolean;
  /** WORKSPACE research context (field/lab-size). Personal role is patched
   *  to the user separately — never sent through the workspace endpoint. */
  onboardingProfile?: WorkspaceResearchContext;
}

export function createWorkspace(http: HttpClient, workspaceData: CreateWorkspaceParams) {
  return http.post('/workspaces', workspaceData);
}

export function getWorkspace(http: HttpClient, workspaceId: string) {
  return http.get(`/workspaces/${workspaceId}`, {
    headers: { 'x-workspace-id': workspaceId },
  });
}

export function getWorkspaceGraph(http: HttpClient, workspaceId: string) {
  return http.get(`/workspaces/${workspaceId}/graph`);
}

export function getCurrentWorkspace(http: HttpClient) {
  return http.get('/workspace');
}

export function getWorkspaces(http: HttpClient) {
  return http.get('/workspaces');
}

/** Body every deletion request carries: the target, named twice. */
export interface ConfirmWorkspaceDeletionParams {
  /** Must equal the `x-workspace-id` header the client sends. */
  workspaceId: string;
  /** The workspace name exactly as typed; the server compares it. */
  confirmName: string;
}

export interface ScheduledWorkspaceDeletion {
  workspaceId: string;
  deletionScheduledFor: string;
  billingCancelled: boolean;
}

/** The owner's "Delete workspace": locks it now, erases it after the grace period. */
export function scheduleWorkspaceDeletion(
  http: HttpClient,
  params: ConfirmWorkspaceDeletionParams,
): Promise<ApiResponse<ScheduledWorkspaceDeletion>> {
  return http.post('/workspace/schedule-deletion', params, {
    headers: { 'x-workspace-id': params.workspaceId },
  });
}

export interface PendingWorkspaceDeletion {
  id: string;
  name: string;
  deletionScheduledFor: string;
}

/** The caller's own workspaces scheduled for deletion and still restorable. */
export function getPendingWorkspaceDeletions(
  http: HttpClient,
): Promise<ApiResponse<PendingWorkspaceDeletion[]>> {
  return http.get('/workspaces/pending-deletion');
}

export function restoreWorkspace(
  http: HttpClient,
  workspaceId: string,
): Promise<ApiResponse<{ workspaceId: string; billingResumed: boolean | null }>> {
  return http.post(`/workspaces/${workspaceId}/restore`, {});
}

/** Immediate, irreversible erasure: the account-deletion flow only. */
export function deleteWorkspaceNow(http: HttpClient, params: ConfirmWorkspaceDeletionParams) {
  return http.delete('/workspace', {
    data: params,
    headers: { 'x-workspace-id': params.workspaceId },
  });
}

export function getWorkspaceStorage(
  http: HttpClient,
  workspaceId?: string,
): Promise<
  ApiResponse<{
    used: number;
    limit: number;
    percentage: number;
    remaining: number;
  }>
> {
  const targetWorkspaceId = workspaceId ?? http.getWorkspaceId();
  if (!targetWorkspaceId) {
    throw new Error('Workspace ID is required');
  }
  return http.get<{ used: number; limit: number; percentage: number; remaining: number }>(
    `/workspaces/${targetWorkspaceId}/storage`,
  );
}

export function getWorkspaceMembers(
  http: HttpClient,
  workspaceId?: string,
  options?: { search?: string; limit?: number },
): Promise<
  ApiResponse<
    Array<{
      id: string;
      name: string;
      email: string;
      role: string;
      isSupportSeat?: boolean;
      isActive?: boolean;
    }>
  >
> {
  const targetWorkspaceId = workspaceId ?? http.getWorkspaceId();
  if (!targetWorkspaceId) {
    throw new Error('Workspace ID is required for getting members');
  }
  const params = new URLSearchParams();
  if (options?.search) params.append('search', options.search);
  if (options?.limit) params.append('limit', options.limit.toString());
  const qs = params.toString();
  return http.get<
    Array<{ id: string; name: string; email: string; role: string; isSupportSeat?: boolean }>
  >(`/workspaces/${targetWorkspaceId}/members${qs ? `?${qs}` : ''}`);
}

export function updateWorkspaceName(http: HttpClient, workspaceId: string, name: string) {
  return http.put('/workspace', { name, workspaceId });
}

export function updateWorkspaceMemberRole(
  http: HttpClient,
  targetWorkspaceId: string,
  userId: string,
  role: string,
) {
  return http.patch(`/workspace/members/${userId}`, { role, workspaceId: targetWorkspaceId });
}

export function removeWorkspaceMember(http: HttpClient, targetWorkspaceId: string, userId: string) {
  return http.request({
    method: 'DELETE',
    url: `/workspace/members/${userId}`,
    data: { workspaceId: targetWorkspaceId },
  });
}

/**
 * A workspace's logo image, by the `logoHash` on its payload. Sent with that
 * workspace's own `x-workspace-id` (the switcher shows workspaces other than
 * the current one); the server checks membership against it.
 */
export async function getWorkspaceLogo(
  http: HttpClient,
  workspaceId: string,
  logoHash: string,
): Promise<Blob> {
  const response = await http.getClient().get(`/workspaces/${workspaceId}/logo/${logoHash}`, {
    responseType: 'blob',
    headers: { 'x-workspace-id': workspaceId },
  });
  return response.data as Blob;
}

/**
 * The image the workspace's current logo was made from, to reopen in the crop
 * dialog. A logo set before the server kept sources has none: the stored
 * square stands in.
 */
export async function getWorkspaceLogoSource(
  http: HttpClient,
  workspaceId: string,
  logoHash: string,
): Promise<Blob> {
  const response = await http
    .getClient()
    .get(`/workspaces/${workspaceId}/logo/${logoHash}/source`, {
      responseType: 'blob',
      headers: { 'x-workspace-id': workspaceId },
      // No source is an answer here, not a failure.
      validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
    });
  if (response.status === 404) return getWorkspaceLogo(http, workspaceId, logoHash);
  return response.data as Blob;
}

/**
 * Set the workspace's logo (admins). The server normalizes the image, trims
 * its empty margins unless `keepFraming` (the admin framed it), and adds a
 * contrast tile unless `background` is `transparent`.
 */
export function uploadWorkspaceLogo(
  http: HttpClient,
  workspaceId: string,
  image: Blob,
  {
    keepFraming = false,
    background = 'auto',
  }: { keepFraming?: boolean; background?: WorkspaceLogoBackground } = {},
) {
  const form = new FormData();
  if (keepFraming) form.append('framing', 'keep');
  if (background === 'transparent') form.append('background', 'transparent');
  form.append('file', image, 'logo');
  return http.put<{ logoHash: string }>('/upload/workspace-logo', form, {
    headers: { 'x-workspace-id': workspaceId, 'Content-Type': 'multipart/form-data' },
  });
}

/** Clear the workspace's logo (admins). */
export function removeWorkspaceLogo(http: HttpClient, workspaceId: string) {
  return http.delete<{ logoHash: null }>('/workspace/logo', {
    headers: { 'x-workspace-id': workspaceId },
  });
}
